import { Inject, Injectable } from '@angular/core';
import { WINDOW } from '@ng-web-apis/common';
import { concatLatestFrom } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, Observable, of } from 'rxjs';
import { filter, first, map, switchMap } from 'rxjs/operators';

import { ApiProduct, ApiProductColor } from '@api/_api/api-catalog/api-products/api-products.models';
import { CartConstants, CartProductTypes } from '@api/cart/cart.models';
import { MediasHelpers } from '@api/catalog/products/medias/medias.helpers';
import { MediaLocation, MediaResolved } from '@api/catalog/products/medias/medias.models';
import { MediasModelsTransformer } from '@api/catalog/products/medias/medias.models.transformer';
import { Invoice, Order } from '@api/order/order.models';
import { ShippingKind } from '@api/shipping/models/shipping.model';
import { PhysicalStoreService } from '@api/store-locator/physical-store/physical-store.service';
import { BrowserOnly } from '@core/data-providers/platform/platform.decorators';
import { CommonWindow } from '@core/domain/models/common-window.model';
import { EcommerceContext } from '@core/domain/models/context.models';
import { Resolutions } from '@core/domain/state/core.state';
import {
    getCmsConfigValue,
    getContext,
    getResolution, getStaticImageUrl,
    getStaticUrl,
    getStoreAndLanguage,
} from '@core/infrastructure/state/core.selectors';
import { StaticResourceService } from '@core/services/static-resource.service';
import { TimestampService } from '@core/services/timestamp.service';
import { getOrderDeliveryDateUseCase } from '@features/order';
import { AkamaiService } from '@shared/services/akamai.service';

@Injectable()
export class ItemOrderService {
    constructor(
        private readonly store: Store,
        private readonly timestampService: TimestampService,
        private readonly translateService: TranslateService,
        private readonly physicalStoreService: PhysicalStoreService,
        private readonly staticResourceService: StaticResourceService,
        @Inject(WINDOW) private readonly window: CommonWindow
    ) {
    }

    public giftImage: string = this.staticResourceService.getImage('/giftTicket/gift-box.jpg', true);

    // TODO ECOMDUTI-18440 MEJORAR CODIGO SEPARANDO EN METODOS MAS PEQUEÑOS
    public getImage(order: Partial<Order>): Observable<string> {
        return this.store.select(getResolution).pipe(
            concatLatestFrom(() => [
                this.store.select(getCmsConfigValue('akamai_impolicy')).pipe(first()),
                this.store.select(getContext).pipe(
                    filter((context) => !!context),
                    first()
                ),
                this.store.select(getStaticUrl),
                this.store.select(getStaticImageUrl).pipe(first()),
                this.store.select(getCmsConfigValue('giftcard_customize')),
            ]),
            map(([resolution, impolicy, context, staticUrl, staticImageUrl, giftcard]) => {
                const width = resolution === Resolutions.MOBILE ? 50 : 100;
                const phCard = order.items[0].productType === CartProductTypes.PHYSICAL_CARD;
                const vrCart = order.items[0].productType === CartProductTypes.VIRTUAL_CARD;

                if (phCard || vrCart) {
                    const image = `${staticUrl}/itxwebstandard/images/giftcard/physical-image.png?t=${this.timestampService.getTs()}`;

                    let giftcardImage: string = image;

                    const template = giftcard.giftcard_templates.find(
                        (giftcardTemplate) => order.items[0].style
                            && order.items[0].style === giftcardTemplate.templateId
                    );

                    if (template) {
                        giftcardImage = `${staticUrl}/${template.pathimage}?t=${this.timestampService.getTs()}`;
                    }

                    return giftcardImage;
                }

                if (order.items[0].fromStore) {
                    return AkamaiService.useAkamaiProductImage(
                        this.window,

                        `${staticImageUrl}${order.items[0].imageUrl}_5_1_16.jpg?t=${this.timestampService.getTs()}`,
                        width,
                        impolicy
                    );
                }
                return AkamaiService.useAkamaiProductImage(
                    this.window,
                    this.getProductColorUrlImage(order.items[0], 'checkout', context),
                    width,
                    impolicy
                );
            })
        );
    }

    // TODO ECOMDUTI-21039 KUBI MEJORAR CODIGO SEPARANDO EN METODOS MAS PEQUEÑOS
    public getImages(order: Partial<Order>) {
        return combineLatest([
            this.store.select(getResolution).pipe(first()),
            this.store.select(getCmsConfigValue('akamai_impolicy')).pipe(first()),
            this.store.select(getContext).pipe(
                filter((context) => !!context),
                first()
            ),
            this.store.select(getCmsConfigValue('giftcard_customize')).pipe(first()),
            this.store.select(getStaticUrl).pipe(first()),
            this.store.select(getStaticImageUrl).pipe(first()),
        ]).pipe(
            map(([resolution, impolicy, context, giftcard, staticUrl, staticImageUrl]) => {
                const width = resolution === Resolutions.MOBILE ? 50 : 100;

                let images = order.items?.map((item) => {
                    const phCard = item.productType === CartProductTypes.PHYSICAL_CARD;
                    const vrCart = item.productType === CartProductTypes.VIRTUAL_CARD;
                    if (item.displayReference === 'XGIFT') {
                        return this.giftImage;
                    }

                    if (item?.reference === CartConstants.PATCHES_REFERENCE) {
                        return `${staticUrl}/media/2022/crm/benefits/cover/free-alteration-service-1050x1400.png`;
                    }

                    if (phCard || vrCart) {
                        const image = `${staticUrl}/itxwebstandard/images/giftcard/physical-image.png?t=${this.timestampService.getTs()}`;

                        let giftcardImage: string = image;

                        const template = giftcard.giftcard_templates.find(
                            (giftcardTemplate) => item.style
                                && item.style === giftcardTemplate.templateId
                        );

                        if (template) {
                            giftcardImage = `${staticUrl}/${template.pathimage}?t=${this.timestampService.getTs()}`;
                        }

                        return giftcardImage;
                    }

                    if (item.fromStore) {
                        return AkamaiService.useAkamaiProductImage(
                            this.window,

                            `${staticImageUrl}${item.imageUrl}_5_1_16.jpg?t=${this.timestampService.getTs()}`,
                            width,
                            impolicy
                        );
                    }
                    return AkamaiService.useAkamaiProductImage(
                        this.window,
                        this.getProductColorUrlImage(item, 'checkout', context),
                        width,
                        impolicy
                    );
                });

                if (images.length > 4) return images.slice(0, 3);
                if (images.length === 4) return images;

                return images;
            })
        );
    }

    public getDeliveryDate(order: Partial<Order>, suborderId: number | string | null): Observable<string> {
        return this.store.select(getStoreAndLanguage).pipe(
            map((storeAndLanguage) => {
                return getOrderDeliveryDateUseCase(this.translateService, storeAndLanguage, order, suborderId);
            })
        );
    }

    public getCountProducts(order: Partial<Order>): number {
        return order.items.filter((item) => {
            if (!item?.name) {
                return false;
            }

            return true;
        }).length;
    }

    public hasSubOrders(order: Partial<Order>) {
        return combineLatest([
            this.store.select(getCmsConfigValue('show_suborders')),
            this.store.select(getStoreAndLanguage),
        ]).pipe(
            switchMap(([showSub, storeAndLanguage]) => {
                let amountSubordersActive = 0;

                if (order.suborders) {
                    amountSubordersActive = order.suborders.length;
                }

                const showSuborders = order.hasSubOrders && showSub && (order.shipping.kind === 'pickup' || order.shipping.kind === 'delivery') && amountSubordersActive > 1;

                if (showSuborders && order.shipping.kind === 'pickup') {
                    return this.physicalStoreService.getStore(
                        storeAndLanguage.storeId,
                        storeAndLanguage.languageId,
                        order.shipping.shippingData.physicalStoreId
                    ).pipe(map(store => store.shippingOnlyForEmployees));
                }
                return of(showSuborders);
            })
        );
    }

    private getProductColorUrlImage(item: ApiProduct, location: MediaLocation, context: EcommerceContext): string {
        const product: ApiProduct = {
            ...item,
            detail: {
                xmedia: item.xmedia as any,
                xmediaDefaultSet: item.xmediaDefaultSet,
                reference: item.reference,
            },
        };

        const selectedColor = product.colors.find((color) => color.id === product.colorId);

        const productColor: ApiProductColor = {
            catentryId: selectedColor?.catentryId,
            id: selectedColor?.id,
            image: selectedColor?.image,
            name: selectedColor?.name,
            reference: selectedColor?.reference,
        };

        const mediasResolved: MediaResolved[] = MediasModelsTransformer.mediasFromProductApi(
            productColor,
            product,
            context.store.details.amandaBaseUrl,
            context.store.details.imageBaseUrl,
            context.config.brandSettings.mediaPathSuffix,
            context.store.details._xmedia,
            null,
            null,
            null
        );
        const mediaArr = MediasHelpers.mediasResolved(mediasResolved, location);
        return mediaArr[0] ? mediaArr[0].path : '';
    }

    public getEcoTicketUrl(order: Partial<Order>, isGuest: boolean) {
        return this.store.select(getContext).pipe(
            concatLatestFrom(() => this.store.select(getResolution)),
            map(([context, resolution]) => {
                let url = 'https://';

                url += resolution === Resolutions.MOBILE
                    ? this.window.inditex.iProxyHostMobile : this.window.inditex.iProxyHostStandard;

                if (this.window.inditex.iProxySecurePort !== 443) {
                    url += ':';
                    url += this.window.inditex.iProxySecurePortMobile;
                }

                url += resolution === Resolutions.MOBILE
                    ? this.window.inditex.iProxyContextPathMobile : this.window.inditex.iProxyContextPathStandard;

                url = `${url}itxrest/${this.window.inditex.iRestVersion}/order/store/${context.store.id}/order/${order.id}/ticket?languageId=${context.currentLanguage.id}`;

                if (isGuest || this.window.inditex.iPage === 'ItxGuestAccountPage') {
                    url = url.replace('/ticket', '/ticket-guest');
                    url = `${url}&guestToken=${order.guestToken}`;
                }

                return url;
            })
        );
    }

    public getInvoiceUrl(order: Partial<Order>, invoiceInfo: Invoice, isGuest: boolean) {
        return this.store.select(getContext).pipe(
            concatLatestFrom(() => this.store.select(getResolution)),
            map(([context, resolution]) => {
                if (!invoiceInfo) {
                    return '';
                }

                let url = 'https://';

                url += resolution === Resolutions.MOBILE
                    ? this.window.inditex.iProxyHostMobile : this.window.inditex.iProxyHostStandard;

                if (this.window.inditex.iProxySecurePort !== 443) {
                    url += ':';
                    url += this.window.inditex.iProxySecurePortMobile;
                }

                url += resolution === Resolutions.MOBILE
                    ? this.window.inditex.iProxyContextPathMobile : this.window.inditex.iProxyContextPathStandard;

                url = `${url}itxrest/1/order/store/${context.store.id}/order/${order.id}/invoice/${invoiceInfo.invoiceId}?languageId=${context.currentLanguage.id}&fileName=${invoiceInfo.invoiceNamePDF}`;

                if (isGuest || this.window.inditex.iPage === 'ItxGuestAccountPage') {
                    url = url.replace('/invoice/', '/invoice-guest/');
                    url = `${url}&guestToken=${order.guestToken}`;
                }

                return url;
            })
        );
    }

    public getOrderStatus(order: Partial<Order>) {
        const shippingKind = this.getShippingKind(order.shipping?.kind);

        const orderTrackingSelected = this.getOrderTrackingSelected(order);
        const statusSuborderTracking = orderTrackingSelected?.statusSuborderTracking;

        return this.setOrderCurrentStatus(order, statusSuborderTracking, shippingKind);
    }

    public getOrderTrackingSelected(order: Partial<Order>) {
        let orderTrackingSelected = null;

        if (order.suborders.length > 1 && this.isPickupShipping(order.shipping.kind)) {
            let suborder = order.suborders.find((_suborder) => _suborder.status === order.status);
            if (!suborder) {
                suborder = order.suborders[0];
            }
            orderTrackingSelected = order.orderTracking[suborder.id];
        } else if (Object.keys(order.orderTracking)[0]) {
            const firstKey = Object.keys(order.orderTracking)[0];
            orderTrackingSelected = order.orderTracking[firstKey];
        }

        return orderTrackingSelected;
    }

    public getShippingKind(kind: ShippingKind) {
        let shippingKind = '';

        switch (kind) {
            case 'delivery':
                shippingKind = 'delivery';
                break;
            case 'pickup':
                shippingKind = 'pickup';
                break;
            default:
                shippingKind = 'droppoint';
                break;
        }

        return shippingKind;
    }

    private isPickupShipping(shippingType) {
        return shippingType === 'droppoint'
            || shippingType === 'dropbox'
            || shippingType === 'pickup';
    }

    public getOrderStatusList(statusSuborderTracking) {
        return statusSuborderTracking ? {
            // Confirmado + En preparación
            preparation: ['R'],
            // En transportista / En reparto
            courier: ['T'],
            // En punto de recogida / En tienda
            dispatch: ['P', 'I'],
            // Entregado
            delivered: ['E'],
            // cancelado
            canceled: ['C'],
        } : {
            // Confirmado
            confirmed: ['Z', 'M', 'N', 'PQ', 'RP', 'A', 'L', 'E'],
            // En preparación
            preparation: ['F', 'G'],
            // En transportista
            courier: ['R', 'S', 'D'],
            // En reparto / En punto de recogida / En tienda
            dispatch: ['U'],
            // Entregado
            delivered: ['K', 'O'],
            // cancelado
            canceled: ['X', 'T'],
        };
    }

    private setOrderCurrentStatus(order, statusSuborderTracking, shippingKind) {
        const orderStatusList = this.getOrderStatusList(statusSuborderTracking);

        const myStatus = statusSuborderTracking || order.status;
        const foundEntry = Object.entries(orderStatusList).find(([, value]) => value.includes(myStatus));

        if (!foundEntry) {
            return;
        }

        const [key] = foundEntry;
        return {
            confirmed: key === 'confirmed' || key === 'preparation' || key === 'courier' || key === 'dispatch' || key === 'delivered',
            preparation: key === 'preparation' || key === 'courier' || key === 'dispatch' || key === 'delivered',
            courier: key === 'courier' || key === 'dispatch' || key === 'delivered',
            dispatch: key === 'dispatch' || key === 'courier' && shippingKind === 'delivery' || key === 'delivered',
            delivered: key === 'delivered',
            canceled: key === 'canceled',
        };
    }

    @BrowserOnly<ItemOrderService, undefined>(undefined)
    public isCancellableOrder(order) {
        return !this.isPositx(order)
            && !this.isCustom(order)
            && this.window.inditex?.iStoreJSON?.details?.allowedCancelStatus?.includes(order.status);
    }

    private isPositx(order) {
        if (!order.payment?.length) {
            return false;
        }

        return order.payment.some((payment) => payment.type.indexOf('POSITX') !== -1 || payment.type.indexOf('POSODITX') !== -1);
    }

    private isCustom(order: Partial<Order>) {
        return !!order.items.reduce((total, item) => item?.customization?.length > 0 ? total + 1 : total, 0);
    }
}
